import React, { useEffect, useState } from 'react';
import { financeiroAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { formatCurrency } from '@/lib/utils';
import { 
  Plus, Save, Trash2, Calculator, Users, DollarSign,
  RefreshCw, Building2, BookOpen
} from 'lucide-react';

export default function BasesRateioPage() {
  const [baseAlunos, setBaseAlunos] = useState([]);
  const [baseReceita, setBaseReceita] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [segmentos, setSegmentos] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filtros
  const [filterCompetencia, setFilterCompetencia] = useState(() => {
    const now = new Date();
    return `${String(now.getMonth() + 1).padStart(2, '0')}/${now.getFullYear()}`;
  });
  
  // Dialog
  const [alunosDialogOpen, setAlunosDialogOpen] = useState(false);
  const [receitaDialogOpen, setReceitaDialogOpen] = useState(false);
  const [formAlunos, setFormAlunos] = useState({
    competencia: '',
    cod_unidade: '',
    cod_seg: '',
    qtd_alunos: ''
  });
  const [formReceita, setFormReceita] = useState({
    competencia: '',
    cod_unidade: '',
    cod_seg: '',
    receita_liquida: ''
  });

  // Edição em massa
  const [editMode, setEditMode] = useState(false);
  const [alunosEdit, setAlunosEdit] = useState({});

  useEffect(() => {
    loadCadastros();
  }, []);

  useEffect(() => {
    if (filterCompetencia) {
      loadBases();
    }
  }, [filterCompetencia]);

  const loadCadastros = async () => {
    try {
      const [unidadesRes, segmentosRes] = await Promise.all([
        financeiroAPI.listUnidades(),
        financeiroAPI.listSegmentos()
      ]);
      setUnidades(unidadesRes.data);
      setSegmentos(segmentosRes.data.filter(s => s.cod_seg !== 'GERAL'));
    } catch (error) {
      toast.error('Erro ao carregar cadastros');
    }
  };

  const loadBases = async () => {
    try {
      setLoading(true);
      const [alunosRes, receitaRes] = await Promise.all([
        financeiroAPI.listBaseAlunos(filterCompetencia),
        financeiroAPI.listBaseReceita(filterCompetencia)
      ]);
      setBaseAlunos(alunosRes.data);
      setBaseReceita(receitaRes.data);
      
      // Inicializar edição
      const editData = {};
      alunosRes.data.forEach(b => {
        editData[`${b.cod_unidade}_${b.cod_seg}`] = b.qtd_alunos;
      });
      setAlunosEdit(editData);
    } catch (error) {
      toast.error('Erro ao carregar bases');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAlunosDialog = () => {
    setFormAlunos({
      competencia: filterCompetencia,
      cod_unidade: unidades[0]?.cod_unidade || '',
      cod_seg: segmentos[0]?.cod_seg || '',
      qtd_alunos: ''
    });
    setAlunosDialogOpen(true);
  };

  const handleCreateAlunos = async () => {
    try {
      if (!formAlunos.cod_unidade || !formAlunos.cod_seg || !formAlunos.qtd_alunos) {
        toast.error('Preencha todos os campos');
        return;
      }
      await financeiroAPI.createBaseAlunos({
        ...formAlunos,
        qtd_alunos: parseInt(formAlunos.qtd_alunos)
      });
      toast.success('Base de alunos salva');
      setAlunosDialogOpen(false);
      loadBases();
    } catch (error) {
      toast.error('Erro ao salvar');
    }
  };

  const handleSaveAllAlunos = async () => {
    try {
      let count = 0;
      for (const unidade of unidades) {
        for (const segmento of segmentos) {
          const key = `${unidade.cod_unidade}_${segmento.cod_seg}`;
          const qtd = parseInt(alunosEdit[key]) || 0;
          if (qtd >= 0) {
            await financeiroAPI.createBaseAlunos({
              competencia: filterCompetencia,
              cod_unidade: unidade.cod_unidade,
              cod_seg: segmento.cod_seg,
              qtd_alunos: qtd
            });
            count++;
          }
        }
      }
      toast.success(`${count} registro(s) salvo(s)`);
      setEditMode(false);
      loadBases();
    } catch (error) {
      toast.error('Erro ao salvar');
    }
  };

  const handleOpenReceitaDialog = () => {
    setFormReceita({
      competencia: filterCompetencia,
      cod_unidade: unidades[0]?.cod_unidade || '',
      cod_seg: segmentos[0]?.cod_seg || '',
      receita_liquida: ''
    });
    setReceitaDialogOpen(true);
  };

  const handleCreateReceita = async () => {
    try {
      if (!formReceita.cod_unidade || !formReceita.cod_seg || !formReceita.receita_liquida) {
        toast.error('Preencha todos os campos');
        return;
      }
      await financeiroAPI.createBaseReceita({
        ...formReceita,
        receita_liquida: parseFloat(formReceita.receita_liquida)
      });
      toast.success('Base de receita salva');
      setReceitaDialogOpen(false);
      loadBases();
    } catch (error) {
      toast.error('Erro ao salvar');
    }
  };

  const handleCalcularReceita = async () => {
    try {
      const res = await financeiroAPI.calcularBaseReceita(filterCompetencia);
      toast.success(`Receita calculada: ${res.data.results?.length || 0} registro(s)`);
      loadBases();
    } catch (error) {
      toast.error('Erro ao calcular receita');
    }
  };

  const getQtdAlunos = (unidade, segmento) => {
    const base = baseAlunos.find(b => b.cod_unidade === unidade && b.cod_seg === segmento);
    return base?.qtd_alunos || 0;
  };

  const getReceita = (unidade, segmento) => {
    const base = baseReceita.find(b => b.cod_unidade === unidade && b.cod_seg === segmento);
    return base?.receita_liquida || 0;
  };

  const getTotalAlunos = (unidade) => {
    return segmentos.reduce((acc, seg) => acc + getQtdAlunos(unidade, seg.cod_seg), 0);
  };

  const getTotalReceita = (unidade) => {
    return segmentos.reduce((acc, seg) => acc + getReceita(unidade, seg.cod_seg), 0);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Bases para Rateio</h1>
          <p className="text-muted-foreground">Configure a quantidade de alunos e receita por segmento</p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            value={filterCompetencia}
            onChange={(e) => setFilterCompetencia(e.target.value)}
            placeholder="MM/YYYY"
            className="w-32"
          />
          <Button variant="outline" onClick={loadBases}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <Tabs defaultValue="alunos" className="space-y-4">
        <TabsList>
          <TabsTrigger value="alunos" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Base de Alunos
          </TabsTrigger>
          <TabsTrigger value="receita" className="flex items-center gap-2">
            <DollarSign className="w-4 h-4" />
            Base de Receita
          </TabsTrigger>
        </TabsList>

        {/* BASE DE ALUNOS */}
        <TabsContent value="alunos" className="space-y-4">
          <div className="flex justify-between items-center">
            <div className="flex gap-2">
              <Button onClick={handleOpenAlunosDialog}>
                <Plus className="w-4 h-4 mr-2" />
                Adicionar
              </Button>
              {editMode ? (
                <>
                  <Button onClick={handleSaveAllAlunos}>
                    <Save className="w-4 h-4 mr-2" />
                    Salvar Todos
                  </Button>
                  <Button variant="outline" onClick={() => { setEditMode(false); loadBases(); }}>
                    Cancelar
                  </Button>
                </>
              ) : (
                <Button variant="outline" onClick={() => setEditMode(true)}>
                  Editar em Massa
                </Button>
              )}
            </div>
          </div>

          {unidades.map(unidade => (
            <Card key={unidade.cod_unidade}>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-primary" />
                    <CardTitle className="text-lg">{unidade.nome_unidade}</CardTitle>
                    <Badge variant="outline">{unidade.cod_unidade}</Badge>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Total de Alunos</p>
                    <p className="text-2xl font-bold text-primary">{getTotalAlunos(unidade.cod_unidade)}</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-4">
                  {segmentos.map(seg => {
                    const key = `${unidade.cod_unidade}_${seg.cod_seg}`;
                    return (
                      <Card key={seg.cod_seg} className="border-l-4 border-l-blue-500">
                        <CardContent className="pt-4">
                          <p className="text-sm font-medium text-muted-foreground">{seg.nome_seg}</p>
                          {editMode ? (
                            <Input
                              type="number"
                              value={alunosEdit[key] || ''}
                              onChange={(e) => setAlunosEdit({ ...alunosEdit, [key]: e.target.value })}
                              className="mt-2"
                              placeholder="0"
                            />
                          ) : (
                            <p className="text-2xl font-bold">{getQtdAlunos(unidade.cod_unidade, seg.cod_seg)}</p>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* BASE DE RECEITA */}
        <TabsContent value="receita" className="space-y-4">
          <div className="flex justify-between items-center">
            <div className="flex gap-2">
              <Button onClick={handleOpenReceitaDialog}>
                <Plus className="w-4 h-4 mr-2" />
                Adicionar Manualmente
              </Button>
              <Button variant="outline" onClick={handleCalcularReceita}>
                <Calculator className="w-4 h-4 mr-2" />
                Calcular dos Lançamentos
              </Button>
            </div>
          </div>

          {unidades.map(unidade => (
            <Card key={unidade.cod_unidade}>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-primary" />
                    <CardTitle className="text-lg">{unidade.nome_unidade}</CardTitle>
                    <Badge variant="outline">{unidade.cod_unidade}</Badge>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Receita Total</p>
                    <p className="text-2xl font-bold text-green-600">{formatCurrency(getTotalReceita(unidade.cod_unidade))}</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-4">
                  {segmentos.map(seg => (
                    <Card key={seg.cod_seg} className="border-l-4 border-l-green-500">
                      <CardContent className="pt-4">
                        <p className="text-sm font-medium text-muted-foreground">{seg.nome_seg}</p>
                        <p className="text-xl font-bold text-green-600">
                          {formatCurrency(getReceita(unidade.cod_unidade, seg.cod_seg))}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      {/* Dialog Alunos */}
      <Dialog open={alunosDialogOpen} onOpenChange={setAlunosDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adicionar Base de Alunos</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Competência</Label>
              <Input
                value={formAlunos.competencia}
                onChange={(e) => setFormAlunos({ ...formAlunos, competencia: e.target.value })}
                placeholder="MM/YYYY"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Unidade</Label>
                <Select value={formAlunos.cod_unidade || undefined} onValueChange={(v) => setFormAlunos({ ...formAlunos, cod_unidade: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {unidades.map(u => (
                      <SelectItem key={u.cod_unidade} value={u.cod_unidade}>{u.nome_unidade}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Segmento</Label>
                <Select value={formAlunos.cod_seg || undefined} onValueChange={(v) => setFormAlunos({ ...formAlunos, cod_seg: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {segmentos.map(s => (
                      <SelectItem key={s.cod_seg} value={s.cod_seg}>{s.nome_seg}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Quantidade de Alunos</Label>
              <Input
                type="number"
                value={formAlunos.qtd_alunos}
                onChange={(e) => setFormAlunos({ ...formAlunos, qtd_alunos: e.target.value })}
                placeholder="0"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAlunosDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleCreateAlunos}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Receita */}
      <Dialog open={receitaDialogOpen} onOpenChange={setReceitaDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adicionar Base de Receita</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Competência</Label>
              <Input
                value={formReceita.competencia}
                onChange={(e) => setFormReceita({ ...formReceita, competencia: e.target.value })}
                placeholder="MM/YYYY"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Unidade</Label>
                <Select value={formReceita.cod_unidade || undefined} onValueChange={(v) => setFormReceita({ ...formReceita, cod_unidade: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {unidades.map(u => (
                      <SelectItem key={u.cod_unidade} value={u.cod_unidade}>{u.nome_unidade}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Segmento</Label>
                <Select value={formReceita.cod_seg || undefined} onValueChange={(v) => setFormReceita({ ...formReceita, cod_seg: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {segmentos.map(s => (
                      <SelectItem key={s.cod_seg} value={s.cod_seg}>{s.nome_seg}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Receita Líquida</Label>
              <Input
                type="number"
                step="0.01"
                value={formReceita.receita_liquida}
                onChange={(e) => setFormReceita({ ...formReceita, receita_liquida: e.target.value })}
                placeholder="0,00"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReceitaDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleCreateReceita}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
